import { z } from "zod";
import { ClaudeModel, DeliveryMode, EffortLevel, OwnerTaskPlace, Platform, ProjectLifecycle } from "./enums";
import { DecisionBinding, PlanContent, SpecContent } from "./entities";
import { NormalizedRunEvent, ProviderAuthMode, RepoFullName } from "./run";

const Title = z.string().trim().min(1, "Titel fehlt").max(200);

export const CreateIdeaInput = z.strictObject({
  title: Title,
  body: z.string().max(10000).default(""),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  origin: z.enum(["web", "mobile"]).default("web"),
});
export type CreateIdeaInput = z.infer<typeof CreateIdeaInput>;

/** Inhalt einer Idee ändern; `expectedUpdatedAt` verhindert, dass ein älterer Stand einen neueren überschreibt. */
export const UpdateIdeaInput = z.strictObject({
  title: Title,
  body: z.string().max(10000),
  tags: z.array(z.string().trim().min(1).max(40)).max(20),
  expectedUpdatedAt: z.string().min(1).max(64),
});
export type UpdateIdeaInput = z.infer<typeof UpdateIdeaInput>;

/** Titel und Ziel eines Projekts ändern. */
export const UpdateProjectInput = z.strictObject({
  title: Title,
  goal: z.string().trim().min(1).max(2000),
});
export type UpdateProjectInput = z.infer<typeof UpdateProjectInput>;

export const SetProjectLifecycleInput = z.strictObject({ lifecycle: ProjectLifecycle });

const ProjectRef = z.string().min(1).max(64);

/** In den Fokus nehmen oder herausnehmen; ist der Fokus voll, nennt `pauseProjectId` das Projekt, das pausiert. */
export const SetProjectFocusInput = z.strictObject({
  focus: z.boolean(),
  pauseProjectId: ProjectRef.optional(),
});
export type SetProjectFocusInput = z.infer<typeof SetProjectFocusInput>;

const CriterionText = z.string().trim().min(1, "Kriterium ist leer").max(300);

/** „Fertig heißt“ und „Später“ eines Projekts als Ganzes speichern (höchstens sieben Kriterien). */
export const SetProjectDoneInput = z.strictObject({
  criteria: z
    .array(z.strictObject({ text: CriterionText, met: z.boolean() }))
    .max(7, "Höchstens sieben Kriterien"),
  later: z.array(CriterionText).max(50),
});
export type SetProjectDoneInput = z.infer<typeof SetProjectDoneInput>;

/** Auslieferungsweg eines Projekts; bei `manual` die Schritte, die der Owner nach dem Mergen abhakt. */
export const SetProjectDeliveryInput = z.strictObject({
  mode: DeliveryMode,
  steps: z.array(z.string().trim().min(1, "Schritt ist leer").max(300)).max(12, "Höchstens zwölf Schritte"),
});
export type SetProjectDeliveryInput = z.infer<typeof SetProjectDeliveryInput>;

/** Neuer Eintrag in „Für dich zu tun“; Zeilen mit `$ ` am Anfang sind Befehle zum Kopieren. */
export const CreateOwnerTaskInput = z.strictObject({
  title: Title,
  projectId: z.string().min(1).max(64).nullable().default(null),
  place: OwnerTaskPlace,
  urgent: z.boolean().default(false),
  note: z.string().trim().max(2000).default(""),
  steps: z.array(z.string().trim().min(1).max(2000)).max(30).default([]),
});
export type CreateOwnerTaskInput = z.infer<typeof CreateOwnerTaskInput>;

export const SetOwnerTaskDoneInput = z.strictObject({ done: z.boolean() });

/** Auftrag als „bereits umgesetzt“ abschließen (QA-08); die Notiz landet im Abbruchgrund. */
export const AlreadyDoneInput = z.strictObject({ note: z.string().trim().max(300).default("") });

export const UpdateIdeaMaturityInput = z.strictObject({
  maturity: z.enum(["CLARIFIED", "READY", "PARKED", "DISCARDED", "RAW"]),
});

export const PromoteIdeaInput = z.strictObject({
  profileKey: z.string().min(1).max(40),
  platform: Platform,
  /** Gleich in den Fokus nehmen; bei vollem Fokus pausiert dafür `pauseProjectId`. */
  focus: z.boolean().default(false),
  pauseProjectId: ProjectRef.optional(),
});

export const CreateProjectInput = z.strictObject({
  title: Title,
  goal: z.string().trim().min(1).max(2000),
  platform: Platform,
  profileKey: z.string().min(1).max(40),
});
export type CreateProjectInput = z.infer<typeof CreateProjectInput>;

export const CreateWorkOrderInput = z.strictObject({
  title: Title,
  kind: z.enum(["new_app", "feature", "bugfix", "audit", "research"]).default("feature"),
  budgetUsd: z.number().nonnegative().max(1000).default(5),
});
export type CreateWorkOrderInput = z.infer<typeof CreateWorkOrderInput>;

const Indices = z.array(z.number().int().min(0).max(49)).max(50);

/**
 * Auftrag teilen (UX-10): die gewählten Muss-Funktionen wandern in einen neuen Auftrag. Die Akzeptanzkriterien
 * ordnet szyOS selbst zu; `acceptance` übernimmt eine im Studio geprüfte oder korrigierte Zuordnung.
 */
export const SplitWorkOrderInput = z.strictObject({
  title: Title,
  mustHaves: Indices.min(1, "Mindestens eine Funktion für Auftrag 2 wählen"),
  acceptance: Indices.optional(),
  expectedVersion: z.number().int(),
});
export type SplitWorkOrderInput = z.infer<typeof SplitWorkOrderInput>;

export const SubmitSpecInput = z.strictObject({ spec: SpecContent, expectedVersion: z.number().int() });
export const SubmitPlanInput = z.strictObject({ plan: PlanContent, expectedVersion: z.number().int() });

export const ApproveInput = z.strictObject({
  expectedVersion: z.number().int(),
  /** Hash, den der Mensch gesehen hat. Weicht er vom aktuellen ab, wird die Freigabe abgewiesen. */
  approvedHash: z.string(),
});

export const ResolveDecisionInput = z.strictObject({
  optionId: z.string().min(1),
  /** Bindung, die der Mensch gesehen hat; muss mit der gespeicherten übereinstimmen. */
  binding: DecisionBinding,
  /** Abgehakte Schritte der Checkliste (Indizes), nur bei der Auslieferung von Hand. */
  checked: z.array(z.number().int().nonnegative()).max(50).optional(),
});
export type ResolveDecisionInput = z.infer<typeof ResolveDecisionInput>;

export const KillSwitchInput = z.strictObject({
  scope: z.enum(["workspace", "work_order"]),
  workOrderId: z.string().optional(),
  reason: z.string().trim().min(1).max(500),
});

/* Node-Kanal */

export const NodeRegisterInput = z.strictObject({
  registrationCode: z.string().min(8).max(200),
  name: z.string().trim().min(1).max(80),
  capabilities: z.array(z.string().max(60)).max(50),
  version: z.string().max(40),
});

/**
 * Heartbeat: Läufe, die der Node gerade ausführt. Der Server verlängert nur deren Leases, damit ein Lauf,
 * den der Node nach einem Neustart nicht mehr kennt, abläuft. Ohne Angabe (ältere Nodes) alle Leases.
 */
export const NodeHeartbeatInput = z.strictObject({
  runIds: z.array(z.string().max(64)).max(20).optional(),
});

export const NodeClaimInput = z.strictObject({
  adapters: z.array(z.string().max(60)).min(1).max(20),
  /** Auf dem Node eingerichtete Auth-Modi. Der Node meldet nur, dass sie eingerichtet sind, nie Zugangsdaten. */
  authModes: z.array(ProviderAuthMode).max(2).default([]),
});

export const NodeRunEventsInput = z.strictObject({
  leaseId: z.string(),
  events: z.array(NormalizedRunEvent).min(1).max(100),
});
export type NodeRunEventsInput = z.infer<typeof NodeRunEventsInput>;

export const NodeLeaseInput = z.strictObject({ leaseId: z.string() });

/** Datei im Änderungssatz eines Laufs; Inhalte base64, Pfade relativ zur Repo-Wurzel. */
export const ChangesetFile = z.discriminatedUnion("action", [
  z.strictObject({
    action: z.literal("upsert"),
    path: z.string().min(1).max(400),
    contentBase64: z.string().max(1_500_000),
    executable: z.boolean().default(false),
  }),
  z.strictObject({ action: z.literal("delete"), path: z.string().min(1).max(400) }),
]);
export type ChangesetFile = z.infer<typeof ChangesetFile>;

/** Änderungssatz eines Laufs an den Git-Broker (M2-03, statt Git-Bundle: siehe docs/20). */
export const NodeChangesetInput = z.strictObject({
  leaseId: z.string(),
  baseSha: z.string().regex(/^[0-9a-f]{40}$/),
  files: z.array(ChangesetFile).min(1).max(300),
});
export type NodeChangesetInput = z.infer<typeof NodeChangesetInput>;

/** Owner ordnet einem Projekt ein GitHub-Repo zu oder löst die Zuordnung (`null`). */
export const ProjectRepositoryInput = z.strictObject({ fullName: RepoFullName.nullable() });

/** Antwort des Owners auf die Rückfragen im Planungsstudio. */
export const StudioAnswerInput = z.strictObject({ text: z.string().trim().min(1).max(8000) });

/** Standard für Modell und Aufwand aller Claude-Läufe im Workspace (Owner). */
export const AgentSettingsInput = z.strictObject({ model: ClaudeModel, effort: EffortLevel });
export type AgentSettingsInput = z.infer<typeof AgentSettingsInput>;

/** Owner schaltet Abo-Modus und API-Reserve für den Workspace (ADR-016). */
export const ProviderAuthInput = z.strictObject({
  subscription: z.boolean(),
  apiKey: z.boolean(),
});
export type ProviderAuthInput = z.infer<typeof ProviderAuthInput>;
